import { customEvent } from "goi_common";

import { ArrowRightIcon } from "@/components/DirectionsIcons";
import { RegionCrematorium } from "@/utils/regionFacilities";

const TIER_LABEL: Record<RegionCrematorium["tier"], string> = {
  inside: "관내 적용",
  near: "인근 적용",
  outside: "관외 적용",
};

/**
 * 화장장 상세를 parlor 자체 페이지로 보낸다.
 *
 * 전에는 platform 의 /crematorium/{companyname}/ 으로 내보냈다. parlor 에
 * 상세가 생겼으므로 도메인 밖으로 링크를 흘릴 이유가 없어졌다. parlor 상세는
 * 이 요금표에 더해 인근 장례식장, 장지, 길찾기까지 갖고 있다.
 *
 * id 는 숫자다. parlor 의 다른 상세(/facility/{id}/, /place/{id}/)와 같은 관례이고,
 * platform 이 쓰는 companyname 방식과 달리 한글 인코딩 문제가 없다.
 *
 * 이벤트 이름(link_crematorium)은 그대로 둔다. 목적지가 바뀌어도 "화장장 상세로
 * 이동" 이라는 뜻은 같고, 이름을 바꾸면 기존 데이터와 끊긴다.
 */
const buildCrematoriumHref = (id: number) => `/crematorium/${id}/`;

interface RegionCrematoriaProps {
  sigunguName: string;
  crematoria: RegionCrematorium[];
  /**
   * 관내 판정이 확정되는 범위인지. 도 단위 시도 페이지에서는 관내 대상이 시군구별로
   * 갈려 확정할 수 없다. 그때는 배지와 단정 문장을 빼고 관내 대상 지역만 보여준다.
   */
  isInsideTierKnown?: boolean;
  /**
   * 이 섹션이 페이지의 주제인지(/region/crematorium/*). 제목에서 "인근" 을 빼고
   * 검색어 그대로("수원시 화장장, 화장터")를 쓴다. 장례식장 페이지에서는 보조
   * 섹션이라 "인근 화장장" 이 맞지만, 화장장 페이지에서는 그 말이 본문과 어긋난다.
   */
  isFocused?: boolean;
  /**
   * 화장장 표면으로 가는 링크. 이 섹션이 보조일 때만 쓴다(장례식장, 장지 페이지).
   * 섹션은 요약이고 그쪽이 전체 목록이라 크롤러가 탈 경로가 된다.
   */
  moreHref?: string;
}

/**
 * 인근 화장장 섹션. 이 페이지의 차별 장치.
 *
 * 같은 화장장이라도 어느 시군구 페이지에서 보느냐에 따라 적용 요금이 달라진다
 * (관내와 관외가 5~10배 차이). 그래서 인접 시군구 페이지들이 같은 시설 목록을
 * 가져도 표시 숫자가 서로 다르다.
 */
export const RegionCrematoria = ({
  sigunguName,
  crematoria,
  isInsideTierKnown = true,
  isFocused = false,
  moreHref,
}: RegionCrematoriaProps) => {
  if (!crematoria.length) return null;

  return (
    <section
      className="rg-section"
      id="crematorium"
      aria-labelledby="rg-crematorium-title"
    >
      <header className="rg-section__head rg-section__head--stack">
        <h2 id="rg-crematorium-title" className="rg-section__title">
          {isFocused
            ? // 개수 앞에 "인근 포함" 을 둔다. 목록에 인근 시설이 섞여 있어서
              // 그냥 "화장장 N곳" 이라고 쓰면 그 지역 소재 시설 수로 읽힌다.
              // "{지역} 화장장" 어구 자체는 끊지 않는다(헤드 쿼리 정확일치)
              `${sigunguName} 화장장, 인근 포함 ${crematoria.length}곳`
            : `${sigunguName} 인근 화장장 ${crematoria.length}곳`}
        </h2>
        <p className="rg-section__desc">
          {isFocused
            ? `${sigunguName}에서 가까운 순으로 안내합니다. 목록에는 다른 지역 시설도 포함됩니다. 화장장은 전국 63곳뿐이라 한 시군구에 없는 경우가 많고, 관내 요금은 시설이 정한 대상 지역에만 적용됩니다. `
            : ""}
          {isInsideTierKnown
            ? `고인이 ${sigunguName}에 주민등록을 둔 경우를 기준으로 적용 요금을 표기했습니다. 관내와 관외 요금은 지자체 조례에 따라 크게 차이 나므로, 관내 시설의 예약 가능 여부를 먼저 확인하시는 편이 좋습니다.`
            : `관내 요금 적용 대상은 시설마다 다릅니다. 각 시설의 관내 대상 지역을 확인하시고, 지역별 적용 요금은 위에서 지역을 선택해 해당 지역 페이지에서 확인하세요.`}
        </p>
      </header>

      <ul className="rg-grid rg-grid--2">
        {crematoria.map((crematorium) => {
          const isInside = isInsideTierKnown && crematorium.tier === "inside";

          return (
            <li key={crematorium.id} className="rg-card">
              <div className="rg-card__titles">
                {isInsideTierKnown && (
                  <span
                    className={`rg-badge${isInside ? " rg-badge--accent" : ""}`}
                  >
                    {TIER_LABEL[crematorium.tier]}
                  </span>
                )}
                <h3 className="rg-card__title rg-card__title--sm">
                  {crematorium.companyName}
                </h3>
              </div>

              <p className="rg-card__meta">
                {crematorium.address}
                {crematorium.distanceKm !== null &&
                  `, 약 ${crematorium.distanceKm}km`}
              </p>

              <div className="rg-fee">
                <div
                  className={`rg-fee__cell${
                    isInside ? " rg-fee__cell--on" : ""
                  }`}
                >
                  <span className="rg-fee__label">관내 요금</span>
                  <strong className="rg-fee__value">
                    {crematorium.insidePrice || "문의"}
                  </strong>
                </div>
                <div
                  className={`rg-fee__cell${
                    isInsideTierKnown && !isInside ? " rg-fee__cell--on" : ""
                  }`}
                >
                  <span className="rg-fee__label">관외 요금</span>
                  <strong className="rg-fee__value">
                    {crematorium.outsidePrice || "문의"}
                  </strong>
                </div>
              </div>

              <p className="rg-card__note">
                {!isInsideTierKnown
                  ? `관내 대상은 ${
                      crematorium.insideRegion || "해당 지자체"
                    }입니다.`
                  : isInside
                  ? `${sigunguName} 주민은 관내 요금 ${crematorium.insidePrice}이 적용됩니다.`
                  : `관내 대상은 ${
                      crematorium.insideRegion || "해당 지자체"
                    }입니다. ${sigunguName} 주민은 관외 요금이 적용됩니다.`}
              </p>

              {/* 감면 조건은 항목이 최대 4개까지 붙어 카드를 밀어내므로 접어둔다.
                  details를 쓰면 JS 상태 없이 동작하고 접힌 내용도 크롤된다. */}
              {crematorium.etcItems.length > 0 && (
                <details
                  className="rg-terms"
                  onToggle={(event) => {
                    if (!event.currentTarget.open) return;
                    customEvent({
                      newGtm: {
                        path: "prlr_region",
                        trigger_and_target: "crematorium_terms",
                      },
                    });
                  }}
                >
                  <summary className="rg-terms__summary">
                    {`감면 조건 ${crematorium.etcItems.length}건 보기`}
                  </summary>
                  <ul className="rg-terms__list">
                    {crematorium.etcItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </details>
              )}

              <a
                className="rg-detail"
                href={buildCrematoriumHref(crematorium.id)}
                onClick={() =>
                  customEvent({
                    newGtm: {
                      path: "prlr_region",
                      trigger_and_target: "link_crematorium",
                    },
                  })
                }
              >
                <span>{`${crematorium.companyName} 자세히 알아보기`}</span>
                <ArrowRightIcon size={13} />
              </a>
            </li>
          );
        })}
      </ul>

      {!isFocused && moreHref && (
        <a
          className="rg-detail rg-detail--foot"
          href={moreHref}
          onClick={() =>
            customEvent({
              newGtm: {
                path: "prlr_region",
                trigger_and_target: "link_crematorium_all",
              },
            })
          }
        >
          <span>{`${sigunguName} 화장장 요금 전체 보기`}</span>
          <ArrowRightIcon size={13} />
        </a>
      )}
    </section>
  );
};
